import React, { useState, useEffect } from 'react';
import { useTheme } from '../theme/ThemeProvider';
import AnimatedBackground from './ui/AnimatedBackground';

const Login = ({ onLogin, loginError, loading }) => {
  const { mode, toggleMode } = useTheme();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [isVisible, setIsVisible] = useState(false);

  // Animación de entrada
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(formData);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--color-light-gray)',
      fontFamily: 'var(--font-family-primary)',
      position: 'relative',
      transition: 'all 0.3s ease',
    },
    backgroundOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: mode === 'dark' 
        ? 'linear-gradient(135deg, #0f1216 0%, #1a1f2e 50%, #151a21 100%)'
        : 'linear-gradient(135deg, #f5f7fa 0%, #ffffff 50%, #f5f7fa 100%)',
      zIndex: 0,
    },
    loginCard: {
      backgroundColor: 'var(--color-white)',
      borderRadius: 'var(--border-radius-xl)',
      padding: 'var(--spacing-3xl)',
      boxShadow: mode === 'dark' ? 'var(--shadow-xl)' : 'var(--shadow-lg)',
      border: mode === 'dark' ? '1px solid #2d3748' : '1px solid var(--color-light-gray)',
      width: '100%',
      maxWidth: '420px',
      margin: '0 auto',
      position: 'relative',
      zIndex: 10,
      transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    themeToggle: {
      position: 'absolute',
      top: 'var(--spacing-lg)',
      right: 'var(--spacing-lg)',
      padding: 'var(--spacing-sm)',
      borderRadius: '50%',
      border: 'none',
      backgroundColor: 'transparent',
      color: 'var(--color-medium-gray)',
      cursor: 'pointer',
      fontSize: 'var(--font-size-xl)',
      transition: 'all 0.2s ease',
      zIndex: 20,
    },
    logo: {
      textAlign: 'center',
      marginBottom: 'var(--spacing-xl)',
      color: 'var(--color-primary-blue)',
      fontSize: 'var(--font-size-3xl)',
      fontWeight: 'var(--font-weight-bold)',
      transform: isVisible ? 'translateY(0)' : 'translateY(-10px)',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.1s',
    },
    title: {
      textAlign: 'center',
      fontSize: 'var(--font-size-2xl)',
      fontWeight: 'var(--font-weight-bold)',
      color: 'var(--color-black)',
      marginBottom: 'var(--spacing-lg)',
      transform: isVisible ? 'translateY(0)' : 'translateY(-10px)',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.2s',
    },
    formGroup: {
      marginBottom: 'var(--spacing-md)',
      transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
      opacity: isVisible ? 1 : 0,
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.3s',
    },
    label: {
      display: 'block',
      marginBottom: 'var(--spacing-xs)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      color: 'var(--color-dark-gray)',
    },
    input: {
      width: '100%',
      padding: 'var(--spacing-md)',
      borderRadius: 'var(--border-radius-md)',
      border: '1px solid var(--color-medium-gray)',
      fontSize: 'var(--font-size-base)',
      fontFamily: 'var(--font-family-primary)',
      backgroundColor: 'var(--color-white)',
      color: 'var(--color-black)',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box',
    },
    button: {
      width: '100%',
      padding: 'var(--spacing-md) var(--spacing-xl)',
      fontSize: 'var(--font-size-base)',
      fontWeight: 'var(--font-weight-semibold)',
      borderRadius: 'var(--border-radius-md)',
      border: 'none',
      backgroundColor: 'var(--color-primary-blue)',
      color: 'var(--color-white)',
      cursor: loading ? 'not-allowed' : 'pointer',
      opacity: loading ? 0.7 : 1,
      transition: 'all 0.3s ease',
      marginTop: 'var(--spacing-md)',
      transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
      position: 'relative',
      overflow: 'hidden',
    },
    secondaryButton: {
      width: '100%',
      padding: 'var(--spacing-md) var(--spacing-xl)',
      fontSize: 'var(--font-size-base)',
      fontWeight: 'var(--font-weight-semibold)',
      borderRadius: 'var(--border-radius-md)',
      border: '2px solid var(--color-primary-blue)',
      backgroundColor: 'transparent',
      color: 'var(--color-primary-blue)',
      cursor: loading ? 'not-allowed' : 'pointer',
      opacity: loading ? 0.7 : 1,
      transition: 'all 0.3s ease',
      marginTop: 'var(--spacing-md)',
    },
    error: {
      backgroundColor: mode === 'dark' ? 'rgba(220, 38, 38, 0.1)' : '#fee2e2',
      color: mode === 'dark' ? '#fca5a5' : '#dc2626',
      padding: 'var(--spacing-sm)',
      borderRadius: 'var(--border-radius-sm)',
      marginBottom: 'var(--spacing-md)',
      fontSize: 'var(--font-size-sm)',
      textAlign: 'center',
      border: `1px solid ${mode === 'dark' ? '#7f1d1d' : '#fed7d7'}`,
      animation: 'shake 0.5s ease-in-out',
    },
    loadingText: {
      textAlign: 'center',
      color: 'var(--color-medium-gray)',
      fontSize: 'var(--font-size-sm)',
      marginTop: 'var(--spacing-sm)',
    }
  };

  const handleInputFocus = (e) => {
    e.target.style.borderColor = 'var(--color-primary-blue)';
    e.target.style.boxShadow = '0 0 0 3px rgba(41, 181, 232, 0.1)';
    e.target.style.transform = 'translateY(-1px)';
  };

  const handleInputBlur = (e) => {
    e.target.style.borderColor = 'var(--color-medium-gray)';
    e.target.style.boxShadow = 'none';
    e.target.style.transform = 'translateY(0)';
  };

  const handleButtonHover = (e) => {
    if (!loading) {
      e.target.style.transform = 'translateY(-2px)';
      e.target.style.boxShadow = 'var(--shadow-lg)';
      e.target.style.backgroundColor = 'var(--color-primary-dark-blue)';
    }
  };

  const handleButtonLeave = (e) => {
    e.target.style.transform = 'translateY(0)';
    e.target.style.boxShadow = 'none';
    e.target.style.backgroundColor = 'var(--color-primary-blue)';
  };

  return (
    <>
      <style>
        {`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-5px); }
            75% { transform: translateX(5px); }
          }

          @keyframes buttonRipple {
            0% {
              transform: scale(0);
              opacity: 1;
            }
            100% {
              transform: scale(4);
              opacity: 0;
            }
          }

          .login-button-ripple {
            position: absolute;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.6);
            transform: scale(0);
            animation: buttonRipple 0.6s ease-out;
            pointer-events: none;
          }

          .theme-toggle:hover {
            background-color: var(--color-light-gray) !important;
            transform: scale(1.1) !important;
          }

          .login-loading-spinner {
            display: inline-block;
            width: 16px;
            height: 16px;
            border: 2px solid transparent;
            border-top: 2px solid currentColor;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-right: 8px;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <div style={styles.container}>
        {/* Fondo animado */}
        <div style={styles.backgroundOverlay} />
        <AnimatedBackground variant="login" intensity="subtle" />
        
        {/* Botón de cambio de tema */}
        <button
          className="theme-toggle"
          style={styles.themeToggle}
          onClick={toggleMode}
          title={mode === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
        >
          {mode === 'dark' ? '☀️' : '🌙'}
        </button>
        
        <div style={styles.loginCard}>
          <div style={styles.logo}>
            Gaero Data Science
          </div>
          
          <h2 style={styles.title}>Iniciar Sesión</h2>
          
          {loginError && (
            <div style={styles.error}>
              ⚠️ {loginError}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label htmlFor="username" style={styles.label}>
                Usuario
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                style={styles.input}
                placeholder="Ingresa tu nombre de usuario"
                required
                disabled={loading}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label htmlFor="password" style={styles.label}>
                Contraseña
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                style={styles.input}
                placeholder="Ingresa tu contraseña"
                required
                disabled={loading}
              />
            </div>
            
            <button
              type="submit"
              style={styles.button}
              onMouseEnter={handleButtonHover}
              onMouseLeave={handleButtonLeave}
              disabled={loading || !formData.username || !formData.password}
            >
              {loading && <span className="login-loading-spinner" />}
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>
          </form>
          
          {/* Botón de registro */}
          <div style={{ textAlign: 'center', marginTop: 'var(--spacing-md)' }}>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => alert('Funcionalidad de registro en desarrollo')}
              disabled={loading}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = 'var(--color-primary-blue)';
                  e.target.style.color = 'var(--color-white)';
                  e.target.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = 'var(--color-primary-blue)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Registrarse
            </button>
          </div>
          
          {loading && (
            <div style={styles.loadingText}>
              <span className="login-loading-spinner" style={{ marginRight: '8px' }} />
              Verificando credenciales...
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Login;